import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductInventoryDto, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { CustomIncludeModalConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { MAXIMUM_QUANTITY_PER_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import loadVariantChildrenPage from "@thkStore/Store/Pages/ProductDetails/Handlers/LoadVariantChildrenPage";
import {
    selectProductDetailMaximumQuantityPerProduct,
    selectProductDetailsAttributeTypeCustomProperties,
    selectProductDetailsVariantTraitCustomProperties,
} from "@thkStore/Store/Selectors/CustomPropertiesSelectors";
import restoreClipboard from "@thkStore/Store/Widgets/Clipboard/Handlers/RestoreClipboard";
import { mergeCustomProperties } from "@thkStore/Types/CustomPropertiesByParentModel";
import { VariantGridFields, VariantGridOwnProps } from "@thkStore/Types/VariantChildrenGridModel";
import { useServerSideVariantChildren } from "@thkStore/Widgets/VariantChildrenGrid/hooks/useServerSideVariantChildren";
import { getIncludesText } from "@thkStore/Widgets/VariantChildrenGrid/utils";
import { VariantChildrenGridSortSelect } from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridSortSelect";
import VariantDataTable from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridVariantDataTable";
import { VariantFilterDrawer } from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridVariantFilterDrawer";
import { VariantPagination } from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridVariantPagination";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

const FilterSortWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        justify-content: space-between;
        gap: 15px;
        padding-top: 16px;
        border-top: ${({ theme }) => theme.colors.common.border} solid 1px;
    }

    [class^="FormFieldStyle-sc-"] {
        width: auto;

        @media (max-width: 768px) {
            order: 1;
        }

        [class^="FormInputWrapper-sc-"] {
            width: auto;
        }
    }
`;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const parentProduct = getProductState(state, ownProps.parentProductId).value;
    return {        
        parentProduct,
        customVariantTraitProperties: selectProductDetailsVariantTraitCustomProperties(state, ownProps.parentProductId),        
        maximumQuantityPerProduct: selectProductDetailMaximumQuantityPerProduct(state, ownProps.parentProductId),
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,        
        customAttributeTypeProperties: selectProductDetailsAttributeTypeCustomProperties(
            state,
            ownProps.parentProductId,
        ),        
    };
};

const mapDispatchToProps = {
    restoreClipboard,
    loadVariantChildrenPage,
    loadRealTimePricing,
    loadRealTimeInventory,
};

type Props = VariantGridOwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const VariantChildrenGridWidget: React.FC<Props> = ({
    variantTraits,
    customVariantTraitProperties,
    parentProduct,
    fields,
    maximumQuantityPerProduct,
    parentProductId,    
    restoreClipboard,
    loadVariantChildrenPage,
    loadRealTimePricing,
    loadRealTimeInventory,
}) => {
    const [filterDrawerIsOpen, setFilterDrawerIsOpen] = useState(false);
    const [pricingByProductId, setPricingByProductId] = useState<Record<string, ProductPriceDto>>({});
    const [inventoryByProductId, setInventoryByProductId] = useState<Record<string, ProductInventoryDto>>({});

    const variantTraitsWithCustomProperties = useMemo(
        () => mergeCustomProperties(variantTraits ?? [], customVariantTraitProperties, trait => trait.id),
        [variantTraits, customVariantTraitProperties],
    );

    React.useEffect(() => {
        restoreClipboard({ parentProductId: parentProductId ?? "" });
    }, []);

    const {
        variants,
        attributeFacets,
        attributeFacetsWithCustomProperties,
        searchWithinQueries,
        sortType,
        currentPage,
        pageSize,
        pageSizeOptions,
        totalResults,
        isLoading,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleFilterChange,
        handleRemoveFilter,
        handleClearAllFilters,
        handleAddSearchFilters,
        handleRemoveSearchTerm,
    } = useServerSideVariantChildren({
        productId: parentProductId,        
        defaultPageSize: fields.defaultPageSize,
        pageSizeOptionsStr: fields.pageSizeOptions,
        customAttributeTypeProperties: customVariantTraitProperties,
        loadVariantChildrenPage,
    });
    
    // Batch load realtime pricing for all visible variants
    React.useEffect(() => {
        if (!variants || variants.length === 0) {
            return;
        }

        const productPriceParameters = variants.map(variant => {
            const info = createFromProduct(variant);
            return {
                productId: info.productId,
                unitOfMeasure: info.unitOfMeasure,
                qtyOrdered: info.qtyOrdered,
            };
        });

        loadRealTimePricing({
            productPriceParameters,
            onSuccess: realTimePricing => {
                const pricingMap: Record<string, ProductPriceDto> = {};
                realTimePricing.realTimePricingResults?.forEach(pricing => {
                    pricingMap[pricing.productId] = pricing;
                });
                setPricingByProductId(prev => ({ ...prev, ...pricingMap }));
            },
        });
    }, [variants]);

    // Batch load realtime inventory for all visible variants
    React.useEffect(() => {
        if (!variants || variants.length === 0) {
            return;
        }

        loadRealTimeInventory({
            productIds: variants.map(v => v.id),
            onSuccess: realTimeInventory => {
                const inventoryMap: Record<string, ProductInventoryDto> = {};
                realTimeInventory.realTimeInventoryResults?.forEach(inv => {
                    inventoryMap[inv.productId] = inv;
                });
                setInventoryByProductId(prev => ({ ...prev, ...inventoryMap }));
            },
        });
    }, [variants]);

    const variantsWithMaxQty = useMemo(
        () =>
            variants?.map(variant => ({
                ...variant,
                properties: {
                    ...variant.properties,
                    [MAXIMUM_QUANTITY_PER_PRODUCT]: maximumQuantityPerProduct[variant.id],
                },
            })),
        [variants, maximumQuantityPerProduct],
    );

    const customRailModalConfig: CustomIncludeModalConfig = useMemo(
        () => ({
            headerText: fields.customRailHeaderText,
            image: fields.customRailImage,
            unitLabel: fields.customRailUnitLabel,
            holeDiameterLabel: fields.customRailMountingHolePitchLabel,
            lengthMinLabel: fields.customRailLengthMinLabel,
            lengthMaxLabel: fields.customRailLengthMaxLabel,
            lengthIncrementLabel: fields.customRailLengthIncrementLabel,
            g1MinLabel: fields.customRailG1MinLabel,
            g1MaxLabel: fields.customRailG1MaxLabel,
            g1IncrementLabel: fields.customRailG1IncrementLabel,
            g2MinLabel: fields.customRailG2MinLabel,
            g2MaxLabel: fields.customRailG2MaxLabel,
            g2IncrementLabel: fields.customRailG2IncrementLabel,
            cancelButtonLabel: fields.customRailCancelButtonLabel,
            confirmButtonLabel: fields.customRailConfirmButtonLabel,
        }),
        [fields],
    );

    const handleFilterDrawerClose = useCallback(() => {
        setFilterDrawerIsOpen(false);
    }, []);

    const handleFilterIconClick = useCallback(() => {
        setFilterDrawerIsOpen(prev => !prev);
    }, []);

    // Only hide the entire widget when there is no data and we are NOT loading
    // (i.e. the product genuinely has no variants). During loading, keep the UI visible.
    if (!isLoading && (!variants || variants.length === 0)) {
        return <></>;
    }

    return (
        <StyledWrapper>
            <FilterSortWrapper>
                <VariantChildrenGridSortSelect sortType={sortType} onSortChange={handleSortChange} />
                <VariantFilterDrawer
                    isOpen={filterDrawerIsOpen}
                    onClose={handleFilterDrawerClose}
                    onToggle={handleFilterIconClick}
                    attributeFacets={attributeFacets}
                    attributeFacetsWithCustomProperties={attributeFacetsWithCustomProperties}
                    searchWithinQueries={searchWithinQueries}
                    onClearAllFilters={handleClearAllFilters}
                    onRemoveFilter={handleRemoveFilter}
                    onRemoveSearchTerm={handleRemoveSearchTerm}
                    onAddSearchFilters={handleAddSearchFilters}
                    onFilterChange={handleFilterChange}
                />
            </FilterSortWrapper>

            { isLoading && (!variants || variants.length === 0) ? (
                    <LoadingSpinner size={50} />
                ) : (
                    <LoadingOverlay loading={isLoading}>
                        <VariantDataTable
                            variants={variantsWithMaxQty || []}
                            variantTraitsWithCustomProperties={variantTraitsWithCustomProperties}
                            pricingByProductId={pricingByProductId}
                            inventoryByProductId={inventoryByProductId}
                            includesText={getIncludesText(parentProduct)}
                            parentProductId={parentProductId}
                            customRailModalConfig={customRailModalConfig}
                        />
                    </LoadingOverlay>
                )
            }

            <VariantPagination
                totalResults={totalResults}
                currentPage={currentPage}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
            />
        </StyledWrapper>
    );
};

const filtersTab = { displayName: "Filters & Pagination", sortOrder: 1 };
const customRailTab = { displayName: "Custom Rail", sortOrder: 2 };

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(VariantChildrenGridWidget)),
    definition: {
        group: "Product Details",
        displayName: "Variant Children Grid",
        icon: "table",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: VariantGridFields.defaultPageSize,
                displayName: "Default Page Size",
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: filtersTab,
                min: 1,
                max: 100,
            },
            {
                name: VariantGridFields.pageSizeOptions,
                displayName: "Page Size Options",
                editorTemplate: "TextField",
                defaultValue: "10,25,50,100",
                fieldType: "General",
                tab: filtersTab,
            },
            {
                name: VariantGridFields.customRailHeaderText,
                displayName: "Header Text",
                editorTemplate: "TextField",
                defaultValue: "Custom Rail",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailUnitLabel,
                displayName: "Unit Label",
                editorTemplate: "TextField",
                defaultValue: "Unit:mm",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailMountingHolePitchLabel,
                displayName: "Mounting Hole Pitch Label",
                editorTemplate: "TextField",
                defaultValue: "Mounting Hole Pitch (F)",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailLengthMinLabel,
                displayName: "Length Min Label",
                editorTemplate: "TextField",
                defaultValue: "Min:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailLengthMaxLabel,
                displayName: "Length Max Label",
                editorTemplate: "TextField",
                defaultValue: "Max:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailLengthIncrementLabel,
                displayName: "Length Increment Label",
                editorTemplate: "TextField",
                defaultValue: "Increment:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG1MinLabel,
                displayName: "G1 Min Label",
                editorTemplate: "TextField",
                defaultValue: "Min:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG1MaxLabel,
                displayName: "G1 Max Label",
                editorTemplate: "TextField",
                defaultValue: "Max:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG1IncrementLabel,
                displayName: "G1 Increment Label",
                editorTemplate: "TextField",
                defaultValue: "Increment:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG2MinLabel,
                displayName: "G2 Min Label",
                editorTemplate: "TextField",
                defaultValue: "Min:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG2MaxLabel,
                displayName: "G2 Max Label",
                editorTemplate: "TextField",
                defaultValue: "Max:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailG2IncrementLabel,
                displayName: "G2 Increment Label",
                editorTemplate: "TextField",
                defaultValue: "Increment:",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailCancelButtonLabel,
                displayName: "Cancel Button Label",
                editorTemplate: "TextField",
                defaultValue: "Cancel",
                fieldType: "General",
                tab: customRailTab,
            },
            {
                name: VariantGridFields.customRailConfirmButtonLabel,
                displayName: "Confirm Button Label",
                editorTemplate: "TextField",
                defaultValue: "Confirm",
                fieldType: "General",
                tab: customRailTab,
            },
        ],
    },
};

export default widgetModule;
